import { useEffect, useState } from "react";
import { Testimonial } from "../../../models/WebsiteModels";
import "./WebsiteModal.css";

interface TestimonialModalProps {
  isOpen: boolean;
  mode: "create" | "edit";
  item: Testimonial | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: TestimonialFormValues) => Promise<void>;
}

export interface TestimonialFormValues {
  clientName: string;
  clientTitle: string;
  clientPhotoUrl: string;
  rating: string;
  reviewText: string;
  isFeatured: boolean;
}

const emptyForm: TestimonialFormValues = {
  clientName: "",
  clientTitle: "",
  clientPhotoUrl: "",
  rating: "",
  reviewText: "",
  isFeatured: false,
};

const fromTestimonial = (item: Testimonial): TestimonialFormValues => ({
  clientName: item.clientName,
  clientTitle: item.clientTitle || "",
  clientPhotoUrl: item.clientPhotoUrl || "",
  rating:
    item.rating !== null && item.rating !== undefined ? String(item.rating) : "",
  reviewText: item.reviewText,
  isFeatured: item.isFeatured,
});

type TextKey = Exclude<keyof TestimonialFormValues, "isFeatured">;

const TestimonialModal = ({
  isOpen,
  mode,
  item,
  submitting,
  onClose,
  onSubmit,
}: TestimonialModalProps) => {
  const [form, setForm] = useState<TestimonialFormValues>(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      setForm(emptyForm);
    } else if (item) {
      setForm(fromTestimonial(item));
    }
  }, [isOpen, mode, item]);

  if (!isOpen) return null;

  const setField = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="wbm-overlay">
      <div className="wbm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wbm-modal__header">
          <h3>{mode === "create" ? "Add Testimonial" : "Edit Testimonial"}</h3>
          <button
            className="wbm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="wbm-modal__body" onSubmit={handleSubmit}>
          <div className="wbm-form-group">
            <label htmlFor="wbt-name">Client Name *</label>
            <input
              id="wbt-name"
              type="text"
              value={form.clientName}
              onChange={(e) => setField("clientName", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbt-title">Client Title</label>
            <input
              id="wbt-title"
              type="text"
              value={form.clientTitle}
              onChange={(e) => setField("clientTitle", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbt-photo">Client Photo URL</label>
            <input
              id="wbt-photo"
              type="text"
              placeholder="https://..."
              value={form.clientPhotoUrl}
              onChange={(e) => setField("clientPhotoUrl", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbt-rating">Rating (1–5)</label>
            <input
              id="wbt-rating"
              type="number"
              min={0}
              max={5}
              step={0.5}
              value={form.rating}
              onChange={(e) => setField("rating", e.target.value)}
            />
          </div>

          <div className="wbm-form-group">
            <label htmlFor="wbt-review">Review *</label>
            <textarea
              id="wbt-review"
              rows={4}
              value={form.reviewText}
              onChange={(e) => setField("reviewText", e.target.value)}
            />
          </div>

          <div className="wbm-form-group wbm-form-check">
            <input
              id="wbt-featured"
              type="checkbox"
              checked={form.isFeatured}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isFeatured: e.target.checked }))
              }
            />
            <label htmlFor="wbt-featured">Featured</label>
          </div>

          <div className="wbm-modal__actions">
            <button
              type="button"
              className="wbm-btn wbm-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="wbm-btn wbm-btn--primary"
              disabled={
                submitting || !form.clientName.trim() || !form.reviewText.trim()
              }
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestimonialModal;